import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { NoteType, ResponsiveNoteTemplateVisitType } from "@rebuild/shared";
import OrganizationSelector from "../../components/ui/utils/OrganizationSelector";
import NoteTypeSelector from "../../components/ui/utils/NoteTypeSelector";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganizationSelect } from "../../hooks/useOrganizationSelect";
import MappingTopBox from "../CreatorTools/MappingTool/components/MappingTopBox";
import {
  buildTemplateSaveBody,
  createDraftTemplate,
  getPreferredResponsiveTemplateOrganization,
  getTemplateForType,
  type ResponsiveNoteTemplate,
  type ResponsiveNoteTemplateDraft,
  type ResponsiveNoteTemplateType,
  type TemplateSectionDraft,
} from "./responsiveNoteTemplateForm";
import { archiveResponsiveNoteTemplate, fetchResponsiveNoteTemplates, saveResponsiveNoteTemplate } from "./responsiveNoteTemplatesAPI";

const templateTypeOptions: Array<{
  type: ResponsiveNoteTemplateType;
  title: string;
  description: string;
}> = [
  {
    type: "general",
    title: "General Template",
    description: "Top-level note questions answered once for the responsive note.",
  },
  {
    type: "room",
    title: "Room Template",
    description: "Room-specific questions used when creating a room subpage.",
  },
];

function cloneTemplateForEdit(
  template: ResponsiveNoteTemplate | ResponsiveNoteTemplateDraft,
  type: ResponsiveNoteTemplateType,
  noteType: NoteType,
  visitType: ResponsiveNoteTemplateVisitType,
): ResponsiveNoteTemplateDraft {
  return {
    ...template,
    type,
    noteType,
    visitType,
    sections: [...template.sections].sort((a, b) => a.position - b.position).map((section, index) => ({ ...section, position: index })),
  };
}

function createEmptySection(position: number): TemplateSectionDraft {
  return {
    title: "",
    position,
    context: "",
    followUpPrompt: "",
    skipFollowUpGeneration: false,
  };
}

function reindexSections(sections: TemplateSectionDraft[]): TemplateSectionDraft[] {
  return sections.map((section, index) => ({ ...section, position: index }));
}

const ResponsiveNoteTemplatesPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { organizations, selectedOrganization, setSelectedOrganization } = useOrganizationSelect({ includeTestOrgs: true });
  const [selectedNoteType, setSelectedNoteType] = useState<NoteType>(NoteType.MITIGATION);
  const [selectedVisitType, setSelectedVisitType] = useState<ResponsiveNoteTemplateVisitType>(ResponsiveNoteTemplateVisitType.INITIAL);
  const [selectedType, setSelectedType] = useState<ResponsiveNoteTemplateType>("general");
  const [templates, setTemplates] = useState<ResponsiveNoteTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState<ResponsiveNoteTemplateDraft>(() =>
    createDraftTemplate("general", NoteType.MITIGATION, ResponsiveNoteTemplateVisitType.INITIAL),
  );

  useEffect(() => {
    const preferredOrganization = getPreferredResponsiveTemplateOrganization({
      organizations,
      selectedOrganization,
      preferredOrganizationId: currentUser?.organizationId,
    });
    if (preferredOrganization && preferredOrganization.organizationId !== selectedOrganization.organizationId) {
      setSelectedOrganization(preferredOrganization);
    }
  }, [currentUser?.organizationId, organizations, selectedOrganization, setSelectedOrganization]);

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchResponsiveNoteTemplates({
        organizationId: selectedOrganization.organizationId,
        noteType: selectedNoteType,
        visitType: selectedVisitType,
      });
      setTemplates(data ?? []);
    } catch (error) {
      console.error("Error loading responsive note templates:", error);
      toast.error("Failed to load responsive note templates");
    } finally {
      setIsLoading(false);
    }
  }, [selectedVisitType, selectedNoteType, selectedOrganization.organizationId]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const activeTemplate = useMemo(
    () => getTemplateForType(templates, selectedType) as ResponsiveNoteTemplate | undefined,
    [selectedType, templates],
  );

  useEffect(() => {
    if (activeTemplate) {
      setDraft(cloneTemplateForEdit(activeTemplate, selectedType, selectedNoteType, selectedVisitType));
      return;
    }
    setDraft(createDraftTemplate(selectedType, selectedNoteType, selectedVisitType));
  }, [activeTemplate, selectedVisitType, selectedNoteType, selectedType]);

  const orderedSections = useMemo(() => [...draft.sections].sort((a, b) => a.position - b.position), [draft.sections]);
  const invalidSections = orderedSections.some((section) => section.title.trim().length === 0);
  const canSave = !isLoading && !isSaving && orderedSections.length > 0 && !invalidSections;

  const setSections = (update: (sections: TemplateSectionDraft[]) => TemplateSectionDraft[]) => {
    setDraft((current) => ({
      ...current,
      sections: reindexSections(update([...current.sections].sort((a, b) => a.position - b.position))),
    }));
  };

  const updateSection = (index: number, updates: Partial<TemplateSectionDraft>) => {
    setSections((sections) => sections.map((section, sectionIndex) => (sectionIndex === index ? { ...section, ...updates } : section)));
  };

  const addSection = () => {
    setSections((sections) => [...sections, createEmptySection(sections.length)]);
  };

  const removeSection = (index: number) => {
    setSections((sections) => sections.filter((_, sectionIndex) => sectionIndex !== index));
  };

  const moveSection = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= orderedSections.length) {
      return;
    }
    setSections((sections) => {
      const nextSections = [...sections];
      [nextSections[index], nextSections[nextIndex]] = [nextSections[nextIndex], nextSections[index]];
      return nextSections;
    });
  };

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      const saveBody = buildTemplateSaveBody({
        ...draft,
        type: selectedType,
        noteType: selectedNoteType,
        visitType: selectedVisitType,
      });
      await saveResponsiveNoteTemplate(selectedOrganization.organizationId, saveBody);
      toast.success(`${selectedType === "general" ? "General" : "Room"} template saved`);
      await loadTemplates();
    } catch (error) {
      console.error("Error saving responsive note template:", error);
      toast.error("Failed to save responsive note template");
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!draft.id) {
      setDraft(createDraftTemplate(selectedType, selectedNoteType, selectedVisitType));
      return;
    }
    const confirmed = window.confirm(`Archive this ${selectedType} template? Notes already created from it will not be changed.`);
    if (!confirmed) return;
    setIsSaving(true);
    try {
      await archiveResponsiveNoteTemplate(selectedOrganization.organizationId, draft.id);
      toast.success(`${selectedType === "general" ? "General" : "Room"} template archived`);
      await loadTemplates();
    } catch (error) {
      console.error("Error archiving responsive note template:", error);
      toast.error("Failed to archive responsive note template");
    } finally {
      setIsSaving(false);
    }
  };

  const selectedOption = templateTypeOptions.find((option) => option.type === selectedType) ?? templateTypeOptions[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <MappingTopBox />
      <div className="ml-8 mt-4 pr-8 pb-10">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-800">Responsive Note Templates</h1>
          <p className="max-w-4xl text-sm text-gray-600">
            Configure the one active general template and room template for each organization and note type.
          </p>
        </div>

        <div className="mb-8 grid grid-cols-3 gap-8 rounded-lg bg-white p-6 shadow">
          <OrganizationSelector
            organizations={organizations}
            selectedOrganization={selectedOrganization}
            onSelect={setSelectedOrganization}
            label="Organization"
          />
          <NoteTypeSelector value={selectedNoteType} onChange={setSelectedNoteType} />
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Visit Type</label>
            <select
              value={selectedVisitType}
              onChange={(event) => setSelectedVisitType(event.target.value as ResponsiveNoteTemplateVisitType)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={ResponsiveNoteTemplateVisitType.INITIAL}>Initial Visit</option>
              <option value={ResponsiveNoteTemplateVisitType.SUBSEQUENT}>Subsequent Visit</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-[320px_1fr] gap-6">
          <div className="space-y-3">
            {templateTypeOptions.map((option) => {
              const template = getTemplateForType(templates, option.type);
              const isSelected = selectedType === option.type;
              return (
                <button
                  key={option.type}
                  onClick={() => setSelectedType(option.type)}
                  className={`w-full rounded-lg border p-4 text-left transition-colors ${
                    isSelected ? "border-blue-500 bg-blue-50 shadow-sm" : "border-gray-200 bg-white hover:border-blue-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <h2 className="text-base font-semibold text-gray-900">{option.title}</h2>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${
                        template ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {template ? "Active" : "Not configured"}
                    </span>
                  </div>
                  <p className="mb-3 text-sm text-gray-600">{option.description}</p>
                  <p className="text-xs font-medium text-gray-500">{template?.sections.length ?? 0} sections</p>
                </button>
              );
            })}
          </div>

          <div className="rounded-lg bg-white p-6 shadow">
            <div>
              <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedOption.title}</h2>
                  <p className="mt-1 text-sm text-gray-600">{selectedOption.description}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleArchive}
                    disabled={isSaving || isLoading}
                    className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Archive
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!canSave}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {isSaving ? "Saving..." : "Save Template"}
                  </button>
                </div>
              </div>

              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                </div>
              ) : (
                <div className="space-y-4">
                  {orderedSections.map((section, index) => (
                    <div key={section.id ?? section.position} className="rounded-lg border border-gray-200 p-4">
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <div className="text-sm font-semibold text-gray-700">Section {index + 1}</div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => moveSection(index, -1)}
                            disabled={index === 0}
                            className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            Up
                          </button>
                          <button
                            type="button"
                            onClick={() => moveSection(index, 1)}
                            disabled={index === orderedSections.length - 1}
                            className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            Down
                          </button>
                          <button
                            type="button"
                            onClick={() => removeSection(index)}
                            className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Title</label>
                      <input
                        value={section.title}
                        onChange={(event) => updateSection(index, { title: event.target.value })}
                        className="mb-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Cause of loss"
                      />
                      <label className="mb-2 block text-sm font-medium text-gray-700">LLM Context</label>
                      <textarea
                        value={section.context}
                        onChange={(event) => updateSection(index, { context: event.target.value })}
                        className="mb-4 min-h-28 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Describe what this section captures and phrases a user may say while answering by voice."
                      />
                      <label className="mb-2 block text-sm font-medium text-gray-700">Follow-up Prompt</label>
                      <textarea
                        value={section.followUpPrompt ?? ""}
                        onChange={(event) => updateSection(index, { followUpPrompt: event.target.value })}
                        className="min-h-28 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Prompt used to ask follow-up questions when the section answer needs more detail."
                      />
                      <label className="mt-3 flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          checked={section.skipFollowUpGeneration ?? false}
                          onChange={(event) => updateSection(index, { skipFollowUpGeneration: event.target.checked })}
                          className="h-4 w-4 cursor-pointer"
                        />
                        Skip follow-up generation
                      </label>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addSection}
                    className="w-full rounded-lg border border-dashed border-blue-300 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
                  >
                    Add Section
                  </button>

                  {orderedSections.length === 0 && (
                    <p className="text-sm text-gray-500">Add at least one section before saving this template.</p>
                  )}
                  {invalidSections && <p className="text-sm text-red-600">Every section needs a title before saving.</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponsiveNoteTemplatesPage;
